import { Box, makeStyles, Typography } from "@material-ui/core";
import zIndex from "@material-ui/core/styles/zIndex";
import { TokenInfo } from "@solana/spl-token-registry";
import dayjs from "dayjs";
import Chart from "kaktana-react-lightweight-charts";
import { useSnackbar } from "notistack";
import { useEffect, useMemo, useState } from "react";
import { db } from "../../firebase";
import { useFirestoreQuery } from "../../hooks/useFirestoreQuery";
import { getFormattedPrice } from "../../utils/formattedPrice";

const useStyles = makeStyles((theme) => ({
  container: {
    flex: 1,
    height: "auto",
    [theme.breakpoints.up("sm")]: {
      width: "45vw",
      minHeight: "400px",
    },
    [theme.breakpoints.down("sm")]: {
      width: "70vw",
      minHeight: "300px",
      maxWidth: "440px",
    },
    [theme.breakpoints.down("xs")]: {
      width: "90vw",
      minHeight: "270px",
    },
    boxShadow: "0 20px 40px rgba(0,0,0,.1)",
    background: theme.palette.background.paper,
    backdropFilter: "blur(2px)",
    WebkitBackdropFilter: "blur(2px)",
    border: "1px solid #4f538599",
    padding: theme.spacing(1),
    borderRadius: theme.spacing(0.8),
    zIndex: zIndex.modal - 1300,
  },
}));

type Props = {
  fromTokenInfo: TokenInfo | undefined;
  toTokenInfo: TokenInfo | undefined;
};

type TChatPrice = {
  time: number;
  value: number;
};

function AreaChart({ fromTokenInfo, toTokenInfo }: Props) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [
    { data: dataFrom, loading: loadingFrom, error: errorFrom },
    refetchFrom,
  ] = useFirestoreQuery(
    db
      .collection("serum-prices-1")
      .doc(fromTokenInfo?.symbol === "wSOL" ? "SOL" : fromTokenInfo?.symbol)
  ); // edge-case for wSOL

  const [{ data: dataTo, loading: loadingTo, error: errorTo }, refetchTo] =
    useFirestoreQuery(
      db
        .collection("serum-prices-1")
        .doc(toTokenInfo?.symbol === "wSOL" ? "SOL" : toTokenInfo?.symbol)
    ); // edge-case for wSOL

  const [priceData, setPriceData] = useState<TChatPrice[]>([]);
  const [forceRender, setForcedReRencer] = useState(() => false);

  const [chartPair, setChartPair] = useState(
    `${toTokenInfo?.symbol}/${fromTokenInfo?.symbol}`
  );

  useEffect(() => {
    refetchFrom();
    setChartPair(`${toTokenInfo?.symbol}/${fromTokenInfo?.symbol}`);
  }, [fromTokenInfo]);
  useEffect(() => {
    refetchTo();
    setChartPair(`${toTokenInfo?.symbol}/${fromTokenInfo?.symbol}`);
  }, [toTokenInfo]);

  useEffect(() => {
    const { formattedData, pair, error } = getFormattedPrice(
      dataFrom,
      dataTo,
      fromTokenInfo?.symbol,
      toTokenInfo?.symbol
    );

    if (error && !loadingTo && !loadingFrom) {
      enqueueSnackbar(error, {
        variant: "error",
      });
    }

    setChartPair(pair);
    if (!formattedData?.length && !loadingTo && !loadingFrom) {
      // Sol Price
      db.collection("serum-prices-1")
        .doc("SOL")
        .onSnapshot((doc) => {
          const data = doc.data()?.prices?.map((d: any) => ({
            time: Date.parse(d.time.toDate()) / 1000,
            value: d.price,
          }));
          setPriceData(data);
          if (!!data?.length) {
            setChartPair("SOL/USDC");
          }
          enqueueSnackbar(`Price chart not available for ${pair}`, {
            variant: "error",
          });
        });
    } else {
      setPriceData(formattedData ?? []);
    }
  }, [dataFrom, dataTo]);

  const styleOptions = useMemo(
    () => ({
      alignLabels: true,
      layout: {
        backgroundColor: "transparent",
      },
      watermark: {
        color: "rgba(105, 212, 186, 0.9)",
        visible: true,
        text: chartPair,
        fontSize: 24,
        horzAlign: "left",
        vertAlign: "bottom",
      },
      localization: {
        locale: "en-US",
        timeFormatter: (unix_timestamp: number) => {
          const date = dayjs.unix(unix_timestamp);
          return date.format(`MMM DD, HH:mm (UTC Z)`);
        },
      },
      crosshair: {
        mode: 1,
        horzLine: {
          color: "rgba(124, 128, 185, 1)",
          labelBackgroundColor: "rgba(124, 128, 185, 1)",
          labelVisible: true,
        },
        vertLine: {
          color: "rgba(124, 128, 185, 1)",
          labelBackgroundColor: "rgba(124, 128, 185, 1)",
          labelVisible: true,
        },
      },
      timeScale: {
        rightOffset: 6,
        barSpacing: 3,
        fixLeftEdge: true,
        lockVisibleTimeRangeOnResize: true,
        rightBarStaysOnScroll: true,
        borderColor: "#353C4D",
        visible: true,
        timeVisible: true,
        secondsVisible: false,
      },
      priceScale: {
        mode: 1,
        autoScale: true,
        alignLabels: true,
        borderColor: "#353C4D",
      },
    }),
    [chartPair, forceRender]
  );

  // re-render to make charts auto-scale
  // using priceData in useMemo will not work
  useEffect(() => {
    setForcedReRencer((p) => !p);
  }, [priceData]);

  return (
    <Box my={2} mx="auto" className={classes.container}>
      <Box
        display="flex"
        height="100%"
        alignItems="center"
        justifyContent="center"
      >
        {loadingFrom || loadingTo || !priceData.length ? (
          <Typography variant="h6" color="primary">
            Loading {chartPair} ...
          </Typography>
        ) : (
          <Chart
            options={styleOptions}
            areaSeries={[
              {
                options: {
                  topColor: "rgba(105, 212, 186, 0.56)",
                  bottomColor: "rgba(105, 212, 186, 0.04)",
                  lineColor: "rgba(105, 212, 186, 1)",
                  // topColor: "rgba(124, 128, 185, 0.56)",
                  // bottomColor: "rgba(124, 128, 185, 0.04)",
                  // lineColor: "rgba(124, 128, 185, 1)",
                  lineWidth: 2,
                  // crosshairMarkerBorderColor: "#353C4D",
                  // crosshairMarkerBackgroundColor: "#353C4D",
                },
                data: priceData?.slice(-5000) ?? [],
              },
            ]}
            autoWidth
            autoHeight
            darkTheme
          />
        )}
      </Box>
    </Box>
  );
}

export default AreaChart;
